import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BasketBar } from './components/BasketBar'
import { CheckoutTabWait } from './components/CheckoutTabWait'
import { PaymentProcessingWait } from './components/PaymentProcessingWait'
import { JobIcon } from './components/JobIcon'
import { AlertIcon, CalendarIcon } from './components/icons'
import {
  bookingReference,
  expiredHoldMessage,
  interpretPaymentConfirmation,
  planAfterBooking,
  resolvePaymentReturn,
  submitBooking as submitBookingFlow,
  type BookingSubmissionApi,
  type PaymentReturnOutcome,
} from './flow/bookingSubmission'
import {
  cancelCheckout,
  resumeFromSnapshot,
  settleHostedCheckout,
  type CheckoutCancelApi,
  type CheckoutCancelOutcome,
  type SnapshotResume,
} from './flow/checkoutReturn'
import { buildFlow, nextStep, previousStep, progressFor, type StepId } from './flow/steps'
import { evServicesMessage } from './lib/evServices'
import { buildJobTypes, findMot, isMotService, matchJobIcon, type JobType } from './lib/jobTypes'
import {
  describeTyreSelection,
  orderFitments,
  shownTyreTotals,
  toSizeChoices,
  toTyreSelectionRequest,
  tyreFittingServiceId,
  tyreSizeRequired,
  type TyreSelection,
} from './lib/tyres'
import { buildUpsellPrompts } from './lib/upsells'
import { bestPromotion, type TyrePromotion } from './lib/tyrePromotions'
import {
  describeOffer,
  EMPTY_FILTERS,
  normalizeOffers,
  offerTotal,
  refreshedOffer,
  splitOffersByAxle,
  type TyreOffer,
  type TyreOfferFilters,
} from './lib/tyreOffers'
import { capacityFromSlotCount, startOfMonth } from './lib/calendar'
import { chooseInCategory } from './lib/categoryChoice'
import { resolveDeepLinkService } from './lib/deepLinkService'
import {
  checkoutReturnUrls,
  goToCheckout,
  readFramedPaymentReturn,
  type CheckoutReturnUrls,
} from './lib/checkoutRedirect'
import {
  parseExternalBookingRequest,
  parsePaymentReturn,
  stripWidgetParams,
} from './lib/deeplink'
import {
  addDays,
  formatMoney,
  normalizePrice,
  sanitizeRegistration,
  startOfDay,
  toLocalDateString,
} from './lib/format'
import {
  CONVERSION_STEPS,
  createConversionTracker,
  DIRECT_SOURCE,
  priceBand,
  resolveTrafficSource,
  type BookingOutcome,
  type ConversionDetails,
  type ConversionParams,
  type ConversionStep,
  type ConversionTracker,
} from './lib/conversionEvents'
import { bookingCalendarSummary, buildIcsDataUrl } from './lib/ics'
import {
  buildPaymentPlan,
  cancellationPolicyText,
  requiresCheckout,
  type PaymentPlan,
} from './lib/payment'
import {
  bookingFingerprint,
  clearPendingBooking,
  markPendingCheckout,
  readPendingBooking,
  savePendingBooking,
  type HeldBooking,
  type PendingBookingSnapshot,
} from './lib/pendingBooking'
import { bookingSlotStart, slotDate } from './lib/slotTime'
import { joinBookingNotes } from './lib/bookingNotes'
import {
  basketDuration,
  basketHasUnconfirmedPrice,
  basketTotal,
  describeTotal,
  toBasketItem,
  type BasketItem,
} from './lib/services'
import {
  PRICE_CHANGED_MESSAGE,
  motClassNotTested,
  serviceNotForVehicleMessage,
  motNotTestedMessage,
  repriceBasket,
  servicesForVehicle,
  shownQuotes,
} from './lib/vehiclePricing'
import {
  MOT_HGV_MESSAGE,
  MOT_VEHICLE_ERROR,
  basketHasMotTest,
  isHgv,
  samePlate,
  shouldAskVehicleType,
  withVehicleTypeAnswer,
} from './lib/motVehicleType'
import { MotVehicleTypeQuestion } from './components/MotVehicleTypeQuestion'
import { PublicBookingChannelService } from './services/PublicBookingChannelService'
import { Shell } from './shell/Shell'
import { ConfirmationStep } from './steps/ConfirmationStep'
import { BuildStep } from './steps/BuildStep'
import { ExtrasStep, type MotPrompt } from './steps/ExtrasStep'
import { TyreAttachStep } from './steps/TyreAttachStep'
import { TyreOffersStep } from './steps/TyreOffersStep'
import { TyreStartStep } from './steps/TyreStartStep'
import { TyreStep } from './steps/TyreStep'
import { DetailsStep, type ContactDetails } from './steps/DetailsStep'
import { ScheduleStep } from './steps/ScheduleStep'
import { VehicleStep, type ManualVehicle } from './steps/VehicleStep'
import {
  VEHICLE_TYPE_NEEDED_MESSAGE,
  hasApiErrorCode,
  isSlotConflict,
  isVehicleNotFound,
  vehicleTypeNeededMessage,
} from './lib/apiErrors'
import { isHttpError } from './lib/http'
import { buildWidgetThemeStyle, getWidgetThemeMode } from './theme'
import { isDisplayMode, isFlowType, type BookingWidgetConfig, type DisplayMode, type WidgetEventName } from './types'
import type {
  AvailableJobTimeSlotDTO,
  CreateWidgetBookingRequest,
  DayCapacity,
  LocalDateString,
  ResolveWidgetVehicleResponse,
  UUID,
  WidgetBookingStatus,
  WidgetBootstrapResponse,
  WidgetMotVehicleAnswer,
  WidgetVehicleTypeResponse,
} from './types/PublicBookingChannelTypes'

export interface BookingWidgetProps {
  config: BookingWidgetConfig
  open: boolean
  onOpenChange: (open: boolean) => void
  onEvent?: (name: WidgetEventName, payload?: unknown) => void
  /** Render the floating launcher while closed. Themed from the channel. */
  showLauncher?: boolean
}

/** What the confirmation screen shows, fixed at the moment the booking was taken. */
interface ConfirmedBooking {
  bookingId: UUID
  reference: string
  plan: PaymentPlan
  /** REQUESTED means the garage still has to accept. Null only from an older backend. */
  status: WidgetBookingStatus | null
}

const TOKEN_SKEW_MS = 30_000
const AUTOCHAIN_TERMS = 'https://www.autochain.co.uk/terms/'

function isTokenExpired(expiresAt: Date | null): boolean {
  if (!expiresAt) return true
  return expiresAt.getTime() - Date.now() <= TOKEN_SKEW_MS
}

const SLOT_GONE_MESSAGE = 'That time has just gone. Pick another.'
/** The customer left Checkout and the time was freed (ACA-101). */
const CHECKOUT_LEFT_MESSAGE = 'Payment cancelled. Nothing was charged. Pick a time to book again.'
const CHECKOUT_LEFT_NO_SNAPSHOT_MESSAGE = 'Payment cancelled. Nothing was charged.'
const CHECKOUT_TAB_CANCEL_FAILED = "We couldn't cancel that payment just now. Please try again."

/** A Checkout open in another tab that this widget is waiting on (ACA-99). */
interface CheckoutTab {
  bookingId: UUID
  sessionId: string | null
  checkoutUrl: string
}

/** A Klarna payment Stripe has not confirmed yet (ACA-105). */
interface ProcessingPayment {
  bookingId: UUID
  sessionId: string
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'

/** Every step before payment: on the page Checkout returns to, those were this visit's already. */
/** The steps where the customer settles what they are booking. */
const SERVICE_STEPS: ReadonlySet<StepId> = new Set<StepId>(['build', 'tyres', 'tyreOffers', 'extras'])

const STEPS_BEFORE_PAYMENT: readonly ConversionStep[] = CONVERSION_STEPS.filter(
  (step) => step !== 'payment_completed'
)

/** The visit's source for checkout-session: only one worth carrying, never "direct". */
function checkoutTrafficSource(configured: string | undefined): string | null {
  const source = resolveTrafficSource(configured)
  return source === DIRECT_SOURCE ? null : source
}

/** How a new booking reads in the funnel events: a request, confirmed, or waiting on payment. */
function bookingOutcome(status: WidgetBookingStatus | null, paysOnline: boolean): BookingOutcome {
  if (status === 'REQUESTED') return 'request'
  if (status === 'AWAITING_PAYMENT') return 'awaiting_payment'
  if (status === 'CONFIRMED') return 'confirmed'
  return paysOnline ? 'awaiting_payment' : 'confirmed'
}

function getErrorMessage(error: unknown): string {
  if (isHttpError(error)) {
    // A conflict's server message can be written for the garage's staff.
    if (error.response?.status === 409) return GENERIC_ERROR_MESSAGE

    const data = error.response?.data as unknown
    if (typeof data === 'string' && data.trim()) return data.trim()

    const message = (data as { message?: unknown } | null | undefined)?.message
    if (typeof message === 'string' && message.trim()) return message.trim()
  }

  if (error instanceof Error) return error.message
  return GENERIC_ERROR_MESSAGE
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

let warnedOnlineBookingInactive = false

/**
 * The garage hasn't got the Online Booking add-on (402 FEATURE_REQUIRED). The
 * widget shows nothing to the garage's customers; the site owner gets one
 * console line saying why, however many times the widget mounts.
 */
function warnOnlineBookingInactiveOnce(): void {
  if (warnedOnlineBookingInactive) return
  warnedOnlineBookingInactive = true
  console.warn(
    'AutoChain booking widget: Online Booking is not active for this garage, so the widget is hidden. Add it in AutoChain, Settings, Billing.'
  )
}

/**
 * Booking orchestrator.
 *
 * Owns channel bootstrap, flow navigation and the booking request. Presentation
 * lives in Shell, the journey lives in flow/steps, and every rule worth testing
 * lives in lib/ — so this file stays about coordination.
 */
export function BookingWidget({
  config,
  open,
  onOpenChange,
  onEvent,
  showLauncher = false,
}: BookingWidgetProps) {
  // ----- Channel -----
  const [bootstrap, setBootstrap] = useState<WidgetBootstrapResponse | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)
  const [loadingBootstrap, setLoadingBootstrap] = useState(false)

  // ----- Journey -----
  // Every flow starts on the vehicle; the guard effect below keeps this honest
  // if a flow is ever reordered so that it does not.
  const [stepId, setStepId] = useState<StepId>('vehicle')
  /**
   * Which way the last move went, so the step transition slides with the
   * customer rather than always the same way. Movement that contradicts the
   * gesture reads as a glitch.
   */
  const [navDirection, setNavDirection] = useState<'forward' | 'back'>('forward')
  const [selectedJobKeys, setSelectedJobKeys] = useState<string[]>([])
  const [tyreSelection, setTyreSelection] = useState<TyreSelection | null>(null)
  const [tyreOffers, setTyreOffers] = useState<TyreOffer[]>([])
  const [tyreOffersLoading, setTyreOffersLoading] = useState(false)
  const [tyreOffersError, setTyreOffersError] = useState<string | null>(null)
  const [tyreOffersUnavailable, setTyreOffersUnavailable] = useState(false)
  const [tyreFilters, setTyreFilters] = useState<TyreOfferFilters>(EMPTY_FILTERS)
  const [selectedOffer, setSelectedOffer] = useState<TyreOffer | null>(null)
  // The rear axle of a staggered car (ACA-98): its own offers, filters and choice.
  const [rearTyreOffers, setRearTyreOffers] = useState<TyreOffer[]>([])
  const [rearTyreFilters, setRearTyreFilters] = useState<TyreOfferFilters>(EMPTY_FILTERS)
  const [selectedRearOffer, setSelectedRearOffer] = useState<TyreOffer | null>(null)
  const [registration, setRegistration] = useState('')
  const [vehicle, setVehicle] = useState<ResolveWidgetVehicleResponse | null>(null)
  const [manualVehicle, setManualVehicle] = useState<ManualVehicle | null>(null)
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [lookupError, setLookupError] = useState<string | null>(null)
  /** Set when the customer chose to continue without identifying the vehicle. */
  const [vehicleSkipped, setVehicleSkipped] = useState(false)
  // "What is it?" (ACA-110): asked when the lookup could not tell the MOT class.
  // Nothing is chosen for the customer: the MOT has no price until they answer.
  const [vehicleTypeAnswer, setVehicleTypeAnswer] = useState<WidgetMotVehicleAnswer | null>(null)
  const vehicleTypeRef = useRef<HTMLFieldSetElement>(null)
  /** Bumped to move focus to "What is it?" once it is on screen. */
  const [vehicleTypeFocus, setVehicleTypeFocus] = useState(0)
  /** The server's quotes for the answer, laid over the lookup's. */
  const [answeredVehicle, setAnsweredVehicle] = useState<WidgetVehicleTypeResponse | null>(null)
  const [vehicleTypeBusy, setVehicleTypeBusy] = useState(false)
  const [vehicleTypeError, setVehicleTypeError] = useState<string | null>(null)

  const [basket, setBasket] = useState<BasketItem[]>([])

  const [details, setDetails] = useState<ContactDetails>({ name: '', email: '', phone: '' })
  const [detailErrors, setDetailErrors] = useState<
    Partial<Record<keyof ContactDetails | 'registration', string>>
  >({})
  const [specialRequests, setSpecialRequests] = useState('')

  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()))
  const [capacityByDate, setCapacityByDate] = useState<Record<LocalDateString, DayCapacity>>({})
  const [monthLoading, setMonthLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [slots, setSlots] = useState<AvailableJobTimeSlotDTO[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<AvailableJobTimeSlotDTO | null>(null)

  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState<ConfirmedBooking | null>(null)
  const [paymentBusy, setPaymentBusy] = useState(false)
  const [checkoutTab, setCheckoutTab] = useState<CheckoutTab | null>(null)
  const [checkoutTabError, setCheckoutTabError] = useState<string | null>(null)
  const [processingPayment, setProcessingPayment] = useState<ProcessingPayment | null>(null)

  const api = useMemo(
    () => new PublicBookingChannelService({ apiUrl: config.apiUrl, withCredentials: false }),
    [config.apiUrl]
  )

  const bootstrapInFlight = useRef<Promise<WidgetBootstrapResponse> | null>(null)
  const slotCache = useRef<Map<LocalDateString, AvailableJobTimeSlotDTO[]>>(new Map())
  const lastDeepLinkReg = useRef<string | null>(null)
  /** The last booking link whose service was applied, so it is applied once. */
  const lastDeepLinkService = useRef<string | null>(null)
  /** A category a booking link asked for that still holds a choice; BuildStep opens it. */
  const [deepLinkJobKey, setDeepLinkJobKey] = useState<string | null>(null)
  /** A booking waiting on payment, which a retry reuses rather than duplicating. */
  const heldBooking = useRef<HeldBooking | null>(null)
  /**
   * Set while a basket restored after Stripe lands, so the basket-change reset
   * below keeps the restored slot instead of clearing it.
   */
  const restoredBasketKey = useRef<string | null>(null)

  const channel = bootstrap?.channel ?? null
  // ACA-110: the lookup with the customer's answer to "What is it?" priced in, when they gave one.
  const pricedVehicle = useMemo(
    () => withVehicleTypeAnswer(vehicle, answeredVehicle),
    [answeredVehicle, vehicle]
  )
  // ACA-96/97: every service as this car sees it, priced from the lookup's quotes,
  // with any MOT the garage would not take for it left out.
  // A car the lookup could not find, entered by hand, has nothing to price by: the garage
  // confirms those prices, exactly as the server does.
  const vehicleEnteredByHand = !vehicle && Boolean(manualVehicle?.make.trim())
  const services = useMemo(
    () => servicesForVehicle(bootstrap?.services ?? [], pricedVehicle, vehicleEnteredByHand),
    [bootstrap?.services, pricedVehicle, vehicleEnteredByHand]
  )
  // ACA-125: an electric car the garage doesn't service, and what it can still book.
  const evMessage = useMemo(
    () => evServicesMessage(pricedVehicle, bootstrap?.services ?? []),
    [bootstrap?.services, pricedVehicle]
  )
  // ACA-110: a lorry gets no MOT, and says why; so does a class the garage doesn't test.
  const motUnavailable =
    (bootstrap?.services ?? []).some(isMotService)
      ? isHgv(pricedVehicle)
        ? MOT_HGV_MESSAGE
        : motClassNotTested(pricedVehicle)
          ? motNotTestedMessage(channel?.contactPhone)
          : null
      : null
  const askVehicleType = shouldAskVehicleType(
    vehicle,
    basketHasMotTest(basket, bootstrap?.services ?? []),
    vehicleTypeAnswer
  )
  const showPrices = channel?.showPrices ?? false
  const lookupRequired = channel?.requireVehicleLookup ?? false

  const emit = useCallback(
    (name: WidgetEventName, payload?: unknown) => {
      try {
        onEvent?.(name, payload)
      } catch {
        // A host page's analytics handler must never break the booking flow.
      }
    },
    [onEvent]
  )

  // ----- Conversion events (ACA-107) -----
  // One tracker per visit, so each step is reported once whatever the customer
  // does next. It reaches AutoChain's funnel through reportStep, which is set
  // once the token helpers below exist.
  const conversion = useRef<ConversionTracker | null>(null)
  const reportStep = useRef<(step: ConversionStep, params: ConversionParams) => void>(() => {})
  // A Checkout ending on the hosted page is counted there (ACA-111): this
  // widget still tells the garage's analytics, never AutoChain's funnel twice.
  const hostedPageCountsPayment = useRef(false)
  if (!conversion.current) {
    conversion.current = createConversionTracker({
      source: resolveTrafficSource(config.trafficSource),
      onStep: (step, params) => reportStep.current(step, params),
    })
  }

  // ----- Configuration -----
  const displayMode: DisplayMode = useMemo(() => {
    if (isDisplayMode(config.displayMode)) return config.displayMode
    if (isDisplayMode(channel?.displayMode)) return channel.displayMode
    return 'drawer'
  }, [channel?.displayMode, config.displayMode])

  // The embed wins over the channel, as it does for the display mode: one
  // channel can run the tyres journey on a tyres page and the full one elsewhere.
  const requestedFlowType = useMemo(() => {
    if (isFlowType(config.flowType)) return config.flowType
    if (isFlowType(channel?.flowType)) return channel.flowType
    return 'general'
  }, [channel?.flowType, config.flowType])

  const jobTypes = useMemo(() => buildJobTypes(services), [services])
  const motOffer = useMemo(() => findMot(jobTypes), [jobTypes])

  // A garage with no MOT tester gets no MOT services from the bootstrap. Its MOT
  // channel then runs the general journey, with no error and no reason given:
  // the customer only ever sees what can be booked.
  const flowType = useMemo(() => {
    if (requestedFlowType !== 'mot' || !bootstrap) return requestedFlowType
    return motOffer ? requestedFlowType : 'general'
  }, [bootstrap, motOffer, requestedFlowType])

  const selectedJobs = useMemo(
    () => jobTypes.filter((jobType) => selectedJobKeys.includes(jobType.key)),
    [jobTypes, selectedJobKeys]
  )

  /** Reports a funnel step once per visit, with the journey it happened in. */
  const trackConversion = useCallback(
    (step: ConversionStep, details: ConversionDetails = {}) => {
      conversion.current?.track(step, { flow: flowType, ...details })
    },
    [flowType]
  )

  /**
   * The tyre category, when tyres are being booked.
   *
   * In a tyre-only channel the job is implied rather than chosen on a tile, so
   * it is taken from the catalogue instead of from the selection. Without this
   * the tyre-only flow filtered its own tyre steps out and went straight from
   * the registration to the calendar.
   */
  const tyreJob = useMemo(() => {
    const source = flowType === 'tyres' ? jobTypes : selectedJobs
    return source.find((jobType) => matchJobIcon(jobType.key) === 'tyres') ?? null
  }, [flowType, jobTypes, selectedJobs])

  /**
   * Job types the cross-sell step can still offer.
   *
   * Anything with an option not yet in the basket, rather than anything not yet
   * touched. A customer who took the free brake inspection must still be able to
   * add pads and discs without going back a screen — that is the whole point of
   * building the basket in one place.
   */
  const jobsWithRoomToAdd = useMemo(
    () =>
      jobTypes.filter((jobType) =>
        jobType.services.some((service) => !basket.some((item) => item.serviceId === service.id))
      ),
    [basket, jobTypes]
  )

  /**
   * Whether to ask for a tyre size at all.
   *
   * Only when tyres are actually being bought. A tyre-only channel always is.
   * Otherwise this is decided by the services chosen rather than by the category:
   * a puncture repair, a pressure check and a rotation all sit under "Tyres" too,
   * and none of them depends on a size. Asking somebody to go and read a sidewall
   * for work that does not need it is a screen they should never see.
   *
   * Keyed on the basket, which is safe here because the size step comes after the
   * services step — nothing can remove the step the customer is standing on.
   */
  const tyreSupplyChosen = useMemo(() => {
    if (flowType === 'tyres') return true

    return tyreSizeRequired(
      services.filter((service) => basket.some((item) => item.serviceId === service.id))
    )
  }, [basket, flowType, services])

  const steps = useMemo(
    () =>
      buildFlow({
        flowType,
        includeTyres: tyreSupplyChosen,
        includeTyreOffers: tyreSupplyChosen && !tyreOffersUnavailable,
        includeExtras: jobsWithRoomToAdd.length > 0,
      }),
    [flowType, jobsWithRoomToAdd.length, tyreSupplyChosen, tyreOffersUnavailable]
  )

  /**
   * Drop a size that no longer belongs to anything in the basket.
   *
   * Removing the tyre line on the cross-sell step takes the size step out of the
   * flow, and a stale selection would otherwise still reach the garage as "4 ×
   * 205/55R16" on a booking with no tyres on it.
   */
  useEffect(() => {
    if (!tyreSupplyChosen && tyreSelection) setTyreSelection(null)
  }, [tyreSelection, tyreSupplyChosen])
  const progress = useMemo(() => progressFor(steps, stepId), [steps, stepId])
  const bookingRequested = confirmed?.status === 'REQUESTED'
  // The terminal step is labelled "Booked", which a request is not yet.
  const shownProgress =
    progress && stepId === 'confirmation' && bookingRequested
      ? { ...progress, label: 'Request sent' }
      : progress

  /**
   * The job a single-purpose channel exists to sell. Null on a general channel,
   * where the customer chooses.
   */
  const impliedJob = useMemo(() => {
    if (flowType === 'tyres') return tyreJob
    if (flowType === 'mot') return motOffer?.jobType ?? null
    return null
  }, [flowType, motOffer, tyreJob])

  /**
   * A single-purpose channel has already made the choice for the customer, so
   * select its category up front and put its main service in the basket.
   *
   * An MOT channel used to do neither: it opened on the vehicle, went straight to
   * "Anything else?" with an empty basket, and could reach the details step with
   * nothing to book.
   */
  useEffect(() => {
    if (!impliedJob) return
    if (selectedJobKeys.includes(impliedJob.key)) return

    setSelectedJobKeys((previous) => [...previous, impliedJob.key])

    // The first bookable service in the garage's own display order is their
    // primary job for this channel — the fitting, or the test. Adding it outright
    // guarantees the booking has a line item; anything else in the category (a
    // puncture repair) stays available on the cross-sell step.
    // An MOT filed inside a wider category is that category's MOT, not its first service.
    const main = (flowType === 'mot' ? motOffer?.service : null) ?? impliedJob.bookable[0]
    if (!main) return

    const item = toBasketItem(main)
    setBasket((previous) =>
      previous.some((entry) => entry.serviceId === item.serviceId) ? previous : [...previous, item]
    )
  }, [flowType, impliedJob, motOffer, selectedJobKeys])

  /**
   * Keep the current step inside the flow.
   *
   * Steps appear and disappear as the customer's choices change, and the flow
   * order itself has changed before now. Without this, a step that is no longer
   * part of the journey renders as an empty panel with no way forward.
   */
  useEffect(() => {
    if (steps.length === 0) return
    if (steps.some((step) => step.id === stepId)) return
    setStepId(steps[0].id)
  }, [steps, stepId])

  const themeMode = useMemo(() => getWidgetThemeMode(channel?.themeJson), [channel?.themeJson])
  const themeStyle = useMemo(
    () => buildWidgetThemeStyle(channel?.themeJson, themeMode),
    [channel?.themeJson, themeMode]
  )

  /**
   * Focus goes back to the launcher when the panel closes. The launcher is
   * unmounted while the panel is open, so the focus trap's own restore finds
   * nothing to return to and a keyboard customer was dropped at the top of
   * the host page.
   */
  const launcherRef = useRef<HTMLButtonElement | null>(null)
  const openedFromLauncher = useRef(false)
  useEffect(() => {
    if (open || !openedFromLauncher.current) return
    openedFromLauncher.current = false
    launcherRef.current?.focus()
  }, [open])

  /** A line added before the lookup takes the car's price once the quotes arrive. */
  useEffect(() => {
    // Before the bootstrap there is nothing to price against, and a basket
    // restored after a payment return must not be emptied.
    if (!bootstrap) return
    setBasket((previous) => repriceBasket(previous, services))
  }, [bootstrap, services])

  const serviceTotal = useMemo(() => basketTotal(basket), [basket])

  /**
   * Cost of the chosen tyres, fitted.
   *
   * Not part of the basket: the basket maps to the channel's configured
   * services, and a supplier's tyre is not one of those.
   */
  /** Quantity-based tyre offers the garage has set on the channel. */
  const tyrePromotions = useMemo<TyrePromotion[]>(() => {
    const raw = (channel as { tyrePromotions?: unknown } | null)?.tyrePromotions
    return Array.isArray(raw) ? (raw as TyrePromotion[]) : []
  }, [channel])

  /**
   * Cost of the chosen tyres, fitted, with any qualifying offer applied.
   *
   * Not part of the basket: the basket maps to the channel's configured
   * services, and a supplier's tyre is not one of those.
   */
  const tyreTotal = useMemo(() => {
    if (!tyreSelection) return 0

    // A staggered car (ACA-98) is priced per axle, and only once every axle
    // being fitted has a tyre chosen. Quantity offers are for one size all round.
    const rear = tyreSelection.rear
    if (rear) {
      const frontChosen = tyreSelection.quantity === 0 || selectedOffer != null
      const rearChosen = rear.quantity === 0 || selectedRearOffer != null
      if (!frontChosen || !rearChosen) return 0
      const front = selectedOffer ? offerTotal(selectedOffer, tyreSelection.quantity) : 0
      const back = selectedRearOffer ? offerTotal(selectedRearOffer, rear.quantity) : 0
      return Math.round((front + back) * 100) / 100
    }

    if (!selectedOffer) return 0
    const promo = bestPromotion(tyrePromotions, selectedOffer, tyreSelection.quantity)
    return promo
      ? promo.discountedTotal
      : offerTotal(selectedOffer, tyreSelection.quantity)
  }, [selectedOffer, selectedRearOffer, tyrePromotions, tyreSelection])

  /**
   * A quantity offer the server does not know about (it prices tyres from the
   * offer alone), so its discounted total could not be charged online.
   */
  const tyrePromotionApplied = useMemo(
    () =>
      Boolean(
        tyreSelection &&
          !tyreSelection.rear &&
          selectedOffer &&
          bestPromotion(tyrePromotions, selectedOffer, tyreSelection.quantity)
      ),
    [selectedOffer, tyrePromotions, tyreSelection]
  )

  /**
   * The basket as the customer reads it. Once a tyre is priced fully fitted the
   * fitting service is part of that price, so it shows as included and counts
   * as nothing: the server replaces it with the tyre lines (ACA-98).
   */
  const displayBasket = useMemo(() => {
    if (tyreTotal <= 0) return basket
    const fittingId = tyreFittingServiceId(
      services,
      basket.map((item) => item.serviceId)
    )
    return basket.map((item) =>
      item.serviceId === fittingId
        ? { ...item, price: 0, included: true, priceState: undefined }
        : item
    )
  }, [basket, services, tyreTotal])

  const total = basketTotal(displayBasket) + tyreTotal

  /** The tyres as one basket line: the chosen products, or the sizes until then. */
  const tyreDescription = useMemo(() => {
    if (!tyreSelection) return null

    const rear = tyreSelection.rear
    if (rear && tyreTotal > 0) {
      return [
        selectedOffer && tyreSelection.quantity > 0
          ? describeOffer(selectedOffer, tyreSelection.quantity)
          : null,
        selectedRearOffer && rear.quantity > 0
          ? describeOffer(selectedRearOffer, rear.quantity)
          : null,
      ]
        .filter(Boolean)
        .join(' + ')
    }
    if (!rear && selectedOffer) return describeOffer(selectedOffer, tyreSelection.quantity)
    return describeTyreSelection(tyreSelection)
  }, [selectedOffer, selectedRearOffer, tyreSelection, tyreTotal])

  /** The chosen tyres for the summaries, once they are priced. */
  const pricedTyreLine = useMemo(
    () =>
      tyreTotal > 0 && tyreDescription
        ? { description: tyreDescription, total: tyreTotal }
        : null,
    [tyreDescription, tyreTotal]
  )

  /**
   * Payment plan.
   *
   * Chosen tyres reach the booking as priced lines (ACA-98): the server prices
   * each offer again and puts it on the job in place of the fitting, so the
   * total here is the total the checkout session charges, and the channel's
   * own plan applies to it.
   *
   * The exception is a quantity offer. The server prices tyres from the offer
   * alone, so a discounted total could not be charged online as shown; that
   * booking is paid at the garage instead.
   */
  // A price the garage still has to confirm (ACA-96) is never paid online either.
  const unconfirmedPrice = useMemo(() => basketHasUnconfirmedPrice(displayBasket), [displayBasket])
  const plan = useMemo(() => {
    if (unconfirmedPrice) return buildPaymentPlan(null, total)
    if (tyreTotal > 0) {
      return tyrePromotionApplied ? buildPaymentPlan(null, total) : buildPaymentPlan(channel, total)
    }
    return buildPaymentPlan(channel, serviceTotal)
  }, [channel, serviceTotal, total, tyrePromotionApplied, tyreTotal, unconfirmedPrice])

  /** What the funnel events may say about the basket: categories and a price band, never items. */
  const basketConversionDetails = useMemo<ConversionDetails>(
    () => ({
      serviceCategories: selectedJobs.map((jobType) => jobType.icon),
      priceBand: priceBand(total),
    }),
    [selectedJobs, total]
  )

  const totalDisplay = useMemo(
    () =>
      describeTotal({
        basket: displayBasket,
        tyreTotal,
        // A size chosen with no priced product still means tyres are coming, and
        // the total has to say so rather than quietly omitting them.
        tyresPending: tyreSelection != null,
        depositDue: plan.mode === 'deposit' ? plan.amountDue : null,
        formatMoney,
      }),
    [displayBasket, plan.amountDue, plan.mode, tyreSelection, tyreTotal]
  )

  /** The car's factory tyre fitments, standard first (ACA-98). */
  const tyreFitments = useMemo(() => orderFitments(vehicle?.tyreFitments), [vehicle])

  /**
   * Price of fitting one tyre, taken from the cheapest priced service in the
   * tyre category — that is the garage's own fitting charge, not a guess.
   */
  const tyreFittingPrice = useMemo(() => {
    if (!tyreJob) return null
    return tyreJob.fromPrice
  }, [tyreJob])

  /**
   * The MOT-due prompt. Only shown when we know the expiry, the garage sells
   * MOTs, and the customer has not already added one — "your MOT runs out in
   * 18 days" is a reason to book, but only once.
   */
  const motPrompt = useMemo((): MotPrompt | null => {
    const expiry = vehicle?.motExpiryDate
    if (!expiry || !motOffer) return null

    const { jobType: motJob, service: motService } = motOffer
    if (!motService && selectedJobKeys.includes(motJob.key)) return null
    if (basketHasMotTest(basket, services)) return null

    const expiryDate = new Date(`${expiry}T00:00:00`)
    if (Number.isNaN(expiryDate.getTime())) return null

    const days = Math.round(
      (startOfDay(expiryDate).getTime() - startOfDay(new Date()).getTime()) / 86_400_000
    )

    // Anything beyond a couple of months is not a reason to book today.
    if (days > 62) return null

    return {
      expiryLabel: expiryDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      daysRemaining: days,
      jobType: motJob,
      service: motService,
      fromPrice: motService ? paidPrice(motService.price) : motJob.fromPrice,
    }
  }, [basket, motOffer, selectedJobKeys, services, vehicle?.motExpiryDate])

  /** Reason-led suggestions for the cross-sell step, e.g. alignment after tyres. */
  const upsellPrompts = useMemo(
    () => buildUpsellPrompts(jobsWithRoomToAdd, basket),
    [basket, jobsWithRoomToAdd]
  )

  const minDate = useMemo(() => startOfDay(new Date()), [])
  const maxDate = useMemo(
    () => addDays(minDate, channel?.maxDaysAhead ?? 30),
    [channel?.maxDaysAhead, minDate]
  )

  const basketKey = useMemo(() => basket.map((item) => item.serviceId).join(','), [basket])

  // Availability depends on total duration, so a basket change invalidates it.
  useEffect(() => {
    slotCache.current.clear()
    setSlots([])
    setCapacityByDate({})
    setSlotsError(null)

    if (restoredBasketKey.current === basketKey) {
      // The basket and its slot came back together from a payment round trip.
      restoredBasketKey.current = null
      return
    }
    setSelectedSlot(null)
  }, [basketKey])

  // ----- Bootstrap -----
  const runBootstrap = useCallback(async (): Promise<WidgetBootstrapResponse> => {
    if (!config.publicKey) {
      const message = 'This booking widget is not configured correctly.'
      setBootstrapError(message)
      throw new Error(message)
    }

    setLoadingBootstrap(true)
    setBootstrapError(null)
    try {
      const response = await api.bootstrap(config.publicKey)
      setBootstrap(response)
      setToken(response.token)

      const expires = new Date(response.expiresAt)
      setTokenExpiresAt(Number.isNaN(expires.getTime()) ? null : expires)
      return response
    } catch (error) {
      setBootstrap(null)
      setToken(null)
      setTokenExpiresAt(null)
      setBootstrapError(getErrorMessage(error))
      if (hasApiErrorCode(error, 'FEATURE_REQUIRED')) warnOnlineBookingInactiveOnce()
      throw error
    } finally {
      setLoadingBootstrap(false)
    }
  }, [api, config.publicKey])

  const freshToken = useCallback(async (): Promise<string> => {
    if (token && !isTokenExpired(tokenExpiresAt)) return token

    if (!bootstrapInFlight.current) {
      bootstrapInFlight.current = runBootstrap()
    }

    try {
      const response = await bootstrapInFlight.current
      if (!response.token) throw new Error('Missing widget token')
      return response.token
    } finally {
      bootstrapInFlight.current = null
    }
  }, [runBootstrap, token, tokenExpiresAt])

  useEffect(() => {
    if (bootstrap && token && !isTokenExpired(tokenExpiresAt)) return
    void runBootstrap().catch(() => {
      // Fail closed: the launcher stays hidden until the channel loads.
    })
  }, [bootstrap, runBootstrap, token, tokenExpiresAt])

  // Each funnel step also goes to the host page's own listeners and to
  // AutoChain's anonymous counts. Neither may ever hold up the booking.
  useEffect(() => {
    reportStep.current = (step, params) => {
      emit('conversion', { event: `autochain_${step}`, params })
      if (step === 'payment_completed' && hostedPageCountsPayment.current) return
      void freshToken()
        .then((tokenValue) =>
          api.recordEvent({
            publicKey: config.publicKey,
            event: step,
            source: params.source,
            token: tokenValue,
          })
        )
        .catch(() => {
          // Counting is best effort.
        })
    }
  }, [api, config.publicKey, emit, freshToken])

  /**
   * Adding or removing one service.
   *
   * The only way anything enters the basket, from the build surface or the tyre
   * channel's attach panel. It never navigates: choosing happens where the
   * customer already is, which is the whole reason the separate "pick your
   * options" screen could be deleted.
   */
  const toggleServiceFromExtras = useCallback(
    (jobType: JobType, service: WidgetBootstrapResponse.ServiceItem) => {
      setSubmitError(null)

      const alreadyIn = basket.some((item) => item.serviceId === service.id)

      // Options in one category replace each other (ACA-100).
      setBasket((previous) => chooseInCategory(previous, jobType, service))

      setSelectedJobKeys((previous) => {
        if (alreadyIn) {
          // Drop the category once nothing from it remains, so it returns to
          // the "anything else" list rather than lingering as a chosen job.
          const stillHasOne = jobType.services.some(
            (entry) => entry.id !== service.id && basket.some((item) => item.serviceId === entry.id)
          )
          return stillHasOne ? previous : previous.filter((key) => key !== jobType.key)
        }

        return previous.includes(jobType.key) ? previous : [...previous, jobType.key]
      })

      if (!alreadyIn) {
        emit('services_selected', { serviceIds: [service.id], total })
      }
    },
    [basket, emit, total]
  )

  /**
   * Removing a line from the basket.
   *
   * Clearing the last tyre service also clears the size and chosen product —
   * leaving a £472 tyre attached to a booking with no fitting on it would be a
   * quietly wrong order.
   */
  const removeServiceFromBasket = useCallback(
    (serviceId: string) => {
      setBasket((previous) => previous.filter((item) => item.serviceId !== serviceId))

      const owner = jobTypes.find((jobType) =>
        jobType.services.some((service) => service.id === serviceId)
      )
      if (!owner) return

      const stillHasOne = owner.services.some(
        (service) =>
          service.id !== serviceId && basket.some((item) => item.serviceId === service.id)
      )

      if (!stillHasOne) {
        setSelectedJobKeys((previous) => previous.filter((key) => key !== owner.key))

        if (matchJobIcon(owner.key) === 'tyres') {
          setTyreSelection(null)
          setSelectedOffer(null)
          setSelectedRearOffer(null)
        }
      }
    },
    [basket, jobTypes]
  )

  // ----- "What is it?" (ACA-110) -----
  /** The answer the server holds for the current lookup: none until one is sent. */
  const serverVehicleType = useRef<WidgetMotVehicleAnswer | null>(null)
  /** The customer's answer and the plate it is for, kept across a fresh lookup of that plate. */
  const heldVehicleType = useRef<{ plate: string; answer: WidgetMotVehicleAnswer | null } | null>(null)
  const vehicleTypeRequest = useRef(0)
  /** The server's words when it refused a booking for want of an answer, cleared by one. */
  const vehicleTypeNotice = useRef<string | null>(null)
  const lookupVehicleRef = useRef<(raw: string) => Promise<boolean>>(async () => false)

  /** A different car, or none: the question starts again with nothing chosen. */
  const forgetVehicleType = useCallback(() => {
    vehicleTypeRequest.current += 1
    heldVehicleType.current = null
    serverVehicleType.current = null
    setVehicleTypeAnswer(null)
    setAnsweredVehicle(null)
    setVehicleTypeBusy(false)
    setVehicleTypeError(null)
  }, [])

  /**
   * Tells the server what the vehicle is and takes its new quotes. The server
   * only keeps an answer for a lookup this widget made, so when it has none (a
   * fresh token, or a day gone) the car is looked up again, which sends the
   * answer again; that happens once, never in a loop.
   */
  const sendVehicleType = useCallback(
    async (plate: string, answer: WidgetMotVehicleAnswer, lookUpAgain: boolean) => {
      const request = ++vehicleTypeRequest.current
      setVehicleTypeBusy(true)
      setVehicleTypeError(null)
      try {
        const tokenValue = await freshToken()
        const answered = await api.answerVehicleType({
          publicKey: config.publicKey,
          body: { vehicleReg: plate, vehicleType: answer },
          token: tokenValue,
        })
        if (request !== vehicleTypeRequest.current) return
        serverVehicleType.current = answer
        setAnsweredVehicle(answered)
      } catch (error) {
        if (request !== vehicleTypeRequest.current) return
        if (lookUpAgain && hasApiErrorCode(error, 'VEHICLE_LOOKUP_NEEDED')) {
          await lookupVehicleRef.current(plate)
          return
        }
        // Back to what the prices on screen are for, so the choice and the prices agree.
        const held = serverVehicleType.current
        heldVehicleType.current = { plate, answer: held }
        setVehicleTypeAnswer(held)
        setVehicleTypeError(MOT_VEHICLE_ERROR)
      } finally {
        if (request === vehicleTypeRequest.current) setVehicleTypeBusy(false)
      }
    },
    [api, config.publicKey, freshToken]
  )

  const changeVehicleType = useCallback(
    (answer: WidgetMotVehicleAnswer) => {
      const plate = vehicle?.vehicleReg || registration
      setVehicleTypeAnswer(answer)
      setVehicleTypeError(null)
      // An answer is what "Please tell us what type of vehicle this is." asked for: it goes now.
      setSubmitError((current) =>
        current === VEHICLE_TYPE_NEEDED_MESSAGE || current === vehicleTypeNotice.current ? null : current
      )
      heldVehicleType.current = { plate, answer }
      // The server already holds this answer: nothing to send.
      if (answer === serverVehicleType.current && !vehicleTypeBusy) return
      void sendVehicleType(plate, answer, true)
    },
    [registration, sendVehicleType, vehicle?.vehicleReg, vehicleTypeBusy]
  )

  // ----- Vehicle lookup -----
  const lookupVehicle = useCallback(
    async (raw: string) => {
      const vehicleReg = sanitizeRegistration(raw)
      if (!vehicleReg) {
        setLookupError('Please enter your registration.')
        return false
      }

      setRegistration(vehicleReg)
      setLookupError(null)
      setLookupStatus('loading')

      // One lookup per registration, and it is always free: a car the garage
      // holds, the lookup cache or DVSA. Tyre sizes only come back for a held
      // car; everyone else types theirs on the tyre step.
      try {
        const tokenValue = await freshToken()
        const resolved = await api.resolveVehicle({
          publicKey: config.publicKey,
          body: { vehicleReg },
          token: tokenValue,
        })
        // A new lookup starts again from what it found: the server has dropped any
        // answer to "What is it?", so the customer's answer for this car goes again.
        const plate = resolved.vehicleReg || vehicleReg
        const held = heldVehicleType.current
        vehicleTypeRequest.current += 1
        serverVehicleType.current = null
        setAnsweredVehicle(null)
        setVehicleTypeBusy(false)
        setVehicleTypeError(null)
        if (held && !samePlate(held.plate, plate)) {
          heldVehicleType.current = null
          setVehicleTypeAnswer(null)
        }
        setVehicle(resolved)
        setManualVehicle(null)
        setLookupStatus('success')
        emit('vehicle_looked_up', { registration: vehicleReg, found: true })
        trackConversion('vehicle_found', { vehicle: 'found' })
        if (
          held &&
          samePlate(held.plate, plate) &&
          held.answer &&
          resolved.motClass == null &&
          resolved.quotes
        ) {
          await sendVehicleType(plate, held.answer, false)
        }
        return true
      } catch (error) {
        setVehicle(null)
        setAnsweredVehicle(null)
        setLookupStatus('error')
        setLookupError(
          !lookupRequired
            ? `We couldn't find ${vehicleReg}. You can enter your vehicle details below instead.`
            : isVehicleNotFound(error)
              ? `We couldn't find ${vehicleReg}. Please check the registration and try again.`
              : getErrorMessage(error)
        )
        emit('vehicle_looked_up', { registration: vehicleReg, found: false })
        return false
      }
    },
    [api, config.publicKey, emit, freshToken, lookupRequired, sendVehicleType, trackConversion]
  )
  lookupVehicleRef.current = lookupVehicle

  // ----- Availability -----
  const loadMonthAvailability = useCallback(
    async (cursor: Date) => {
      if (basket.length === 0) return

      setMonthLoading(true)
      try {
        const tokenValue = await freshToken()
        const response = await api.monthAvailability({
          publicKey: config.publicKey,
          month: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
          serviceIds: basket.map((item) => item.serviceId),
          durationMinutes: basketDuration(basket) || config.defaultDurationMinutes,
          token: tokenValue,
        })

        setCapacityByDate((previous) => {
          const next = { ...previous }
          for (const day of response.days) next[day.date] = day.capacity
          return next
        })
      } catch {
        // A month endpoint that is unavailable (or an older backend) must not
        // block booking — the day view still works, it just cannot preview
        // capacity. Deliberately silent.
      } finally {
        setMonthLoading(false)
      }
    },
    [api, basket, config.defaultDurationMinutes, config.publicKey, freshToken]
  )

  const loadSlots = useCallback(
    async (date: Date) => {
      const bookingDate = toLocalDateString(date)
      setSlotsError(null)

      const cached = slotCache.current.get(bookingDate)
      if (cached) {
        setSlots(cached)
        return
      }

      if (basket.length === 0) {
        setSlots([])
        return
      }

      setSlotsLoading(true)
      try {
        const tokenValue = await freshToken()
        const duration = basketDuration(basket) || config.defaultDurationMinutes

        const result = await api.availability({
          publicKey: config.publicKey,
          bookingDate,
          serviceIds: basket.map((item) => item.serviceId),
          durationMinutes: duration,
          token: tokenValue,
        })

        slotCache.current.set(bookingDate, result)
        setSlots(result)

        // Keep the calendar honest even when the month endpoint is absent.
        setCapacityByDate((previous) => ({
          ...previous,
          [bookingDate]: capacityFromSlotCount(result.length),
        }))
      } catch (error) {
        setSlots([])
        setSlotsError(getErrorMessage(error))
      } finally {
        setSlotsLoading(false)
      }
    },
    [api, basket, config.defaultDurationMinutes, config.publicKey, freshToken]
  )

  useEffect(() => {
    if (stepId !== 'schedule') return
    void loadMonthAvailability(monthCursor)
    // loadMonthAvailability changes with the basket, which is the other input
    // that invalidates a month.
  }, [loadMonthAvailability, monthCursor, stepId])

  /**
   * Times for the chosen day, whenever the schedule is on screen without them.
   *
   * Covers a date picked by the customer and one restored after a payment round
   * trip. Always goes through loadSlots, which serves a cached day without a
   * request and is the only place that puts the cached times back on screen.
   */
  useEffect(() => {
    if (stepId !== 'schedule' || !selectedDate) return
    void loadSlots(selectedDate)
  }, [loadSlots, selectedDate, stepId])

  /**
   * Fetch tyres for the chosen size.
   *
   * A supplier that returns nothing (or is not configured at all) drops the
   * product step from the flow entirely rather than showing an empty screen:
   * the booking still captures the size and the garage quotes on arrival.
   */
  const loadTyreOffers = useCallback(
    async (selection: TyreSelection): Promise<{ front: TyreOffer[]; rear: TyreOffer[] } | null> => {
      setTyreOffersLoading(true)
      setTyreOffersError(null)

      // A staggered car asks for both axles at once (ACA-98). An axle with
      // nothing on it still asks for one, so changing its count needs no refetch.
      const rear = selection.rear ?? null

      try {
        const tokenValue = await freshToken()
        const raw = await api.tyreOffers({
          publicKey: config.publicKey,
          size: selection.size.size,
          quantity: Math.max(1, selection.quantity),
          rearSize: rear?.size ?? null,
          rearQuantity: rear ? Math.max(1, rear.quantity) : null,
          token: tokenValue,
        })

        const parsed = normalizeOffers(raw)
        const byAxle = rear ? splitOffersByAxle(parsed) : { front: parsed, rear: [] }
        setTyreOffers(byAxle.front)
        setRearTyreOffers(byAxle.rear)
        // Either axle with nothing to choose from books the sizes for the
        // garage to quote, rather than half a priced order.
        setTyreOffersUnavailable(
          byAxle.front.length === 0 || (rear != null && byAxle.rear.length === 0)
        )
        return byAxle
      } catch {
        // No supplier, or the feed is down. Never block the booking over it.
        setTyreOffers([])
        setRearTyreOffers([])
        setTyreOffersUnavailable(true)
        return null
      } finally {
        setTyreOffersLoading(false)
      }
    },
    [api, config.publicKey, freshToken]
  )

  /**
   * Keyed on the size alone, deliberately not the quantity.
   *
   * A different size is a different list of tyres, so the choice has to go. A
   * different quantity is the same tyre bought more of — prices are per tyre —
   * and clearing the selection there broke the whole point of the "add one more
   * and save" nudge: tapping it threw away the tyre the customer had just
   * chosen.
   */
  const tyreSizeKey = tyreSelection
    ? `${tyreSelection.size.size}|${tyreSelection.rear?.size ?? ''}`
    : null

  useEffect(() => {
    if (!tyreSizeKey || !tyreSelection) return

    setSelectedOffer(null)
    setSelectedRearOffer(null)
    setTyreFilters(EMPTY_FILTERS)
    setRearTyreFilters(EMPTY_FILTERS)
    void loadTyreOffers(tyreSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreSizeKey])

  // ----- Navigation -----
  const validateStep = useCallback(
    (id: StepId): string | null => {
      // An MOT of unknown class (ACA-110): no going on until "What is it?" has an answer.
      if ((id === 'build' || id === 'extras') && askVehicleType && !serverVehicleType.current) {
        setVehicleTypeFocus((n) => n + 1)
        return VEHICLE_TYPE_NEEDED_MESSAGE
      }

      if (id === 'build') {
        // The basket is the answer now that choosing happens in place: a tapped
        // category with options open but nothing chosen has not been decided.
        return basket.length === 0 ? 'Please choose what your car needs.' : null
      }

      if (id === 'vehicle') {
        // Skipping is a deliberate choice, not an empty form.
        if (vehicleSkipped && !lookupRequired) return null
        if (!registration.trim()) return 'Please enter your registration.'
        if (lookupRequired && lookupStatus !== 'success') {
          return 'Please find your vehicle before continuing.'
        }
        if (!lookupRequired && lookupStatus !== 'success') {
          const hasManual = Boolean(manualVehicle?.make.trim() && manualVehicle?.model.trim())
          if (!hasManual) return 'Find your vehicle, or enter its make and model.'
        }
        return null
      }

      if (id === 'tyres') {
        return tyreSelection ? null : 'Please tell us your tyre size.'
      }

      if (id === 'tyreOffers') {
        // Every axle being fitted needs its tyre (ACA-98).
        const frontNeeded = (tyreSelection?.quantity ?? 1) > 0
        const rearNeeded = (tyreSelection?.rear?.quantity ?? 0) > 0
        if (frontNeeded && !selectedOffer) return 'Please choose a tyre.'
        if (rearNeeded && !selectedRearOffer) return 'Please choose a rear tyre.'
        return null
      }

      if (id === 'schedule') {
        return selectedSlot ? null : 'Please choose a drop-off time.'
      }

      if (id === 'details') {
        const errors: Partial<Record<keyof ContactDetails | 'registration', string>> = {}
        // A tyre booking can be priced from a size alone, but it cannot be
        // created without a vehicle, so this is where the plate is required.
        if (!registration.trim()) errors.registration = 'Please enter your registration.'
        if (!details.name.trim()) errors.name = 'Please enter your name.'
        if (!details.email.trim()) errors.email = 'Please enter your email.'
        else if (!isValidEmail(details.email)) errors.email = 'Please check your email address.'
        if (!details.phone.trim()) errors.phone = 'Please enter a phone number.'

        setDetailErrors(errors)
        if (Object.keys(errors).length > 0) return 'Please complete your details.'

        // Details is the last step before we take the booking, so the terms
        // gate lives here rather than beside the calendar.
        if (!termsAccepted) return 'Please accept the terms and conditions.'
        return null
      }

      return null
    },
    [
      askVehicleType,
      basket.length,
      details,
      lookupRequired,
      lookupStatus,
      manualVehicle,
      registration,
      selectedJobKeys,
      selectedOffer,
      selectedRearOffer,
      vehicleSkipped,
      selectedSlot,
      termsAccepted,
      tyreSelection,
    ]
  )

  const goBack = useCallback(() => {
    setSubmitError(null)
    const target = previousStep(steps, stepId)
    if (target) {
      setNavDirection('back')
      setStepId(target.id)
    }
  }, [stepId, steps])

  // ----- Submit -----
  /** The booking calls, bound to a token, in the shape the submission flow expects. */
  const bookingApi = useCallback(
    (
      tokenValue: string,
      returnUrls: CheckoutReturnUrls | null = null
    ): BookingSubmissionApi & CheckoutCancelApi => ({
      createBooking: (request) =>
        api.createBookingRequest({
          publicKey: config.publicKey,
          body: request,
          token: tokenValue,
        }),
      createCheckoutSession: (id, returnUrl) =>
        api.createBookingCheckoutSession({
          publicKey: config.publicKey,
          bookingId: id,
          returnUrl,
          fallbackReturnUrl: returnUrls?.fallbackReturnUrl ?? null,
          framedParent: returnUrls?.framedParent ?? null,
          // Counted where the payment completes, which may be the hosted page (ACA-111).
          trafficSource: checkoutTrafficSource(config.trafficSource),
          token: tokenValue,
        }),
      confirmPayment: (id, sessionId) =>
        api.confirmBookingPayment({
          publicKey: config.publicKey,
          bookingId: id,
          sessionId,
          token: tokenValue,
        }),
      cancelCheckout: (id, sessionId) =>
        api.cancelBookingCheckout({
          publicKey: config.publicKey,
          bookingId: id,
          sessionId,
          token: tokenValue,
        }),
    }),
    [api, config.publicKey, config.trafficSource]
  )

  const submitBooking = useCallback(async () => {
    setSubmitError(null)

    const vehicleReg = registration.trim()
    if (!vehicleReg) {
      setSubmitError('Vehicle registration is required.')
      // Whichever screen actually asks for it in this flow.
      setStepId(steps.some((step) => step.id === 'vehicle') ? 'vehicle' : 'details')
      return
    }

    if (!selectedSlot) {
      setSubmitError('Please choose a drop-off time.')
      setStepId('schedule')
      return
    }

    // Nothing was booked, and any earlier hold is gone with the time.
    const offerOtherTimes = () => {
      heldBooking.current = null
      clearPendingBooking(config.publicKey)
      // Emptying the cache makes the schedule effects fetch fresh times.
      slotCache.current.clear()
      setCapacityByDate({})
      setSlots([])
      setSelectedSlot(null)
      setNavDirection('back')
      setStepId('schedule')
      setSubmitError(SLOT_GONE_MESSAGE)
    }

    setSubmitting(true)
    try {
      const tokenValue = await freshToken()

      const body: CreateWidgetBookingRequest = {
        serviceIds: basket.map((item) => item.serviceId),
        customerName: details.name.trim(),
        customerEmail: details.email.trim(),
        customerPhone: details.phone.trim(),
        // Garage time with its offset, so the server cannot read it in another zone.
        slotStart: bookingSlotStart(selectedSlot, channel?.timezone),
        vehicleReg,
        // What the customer was shown for each line (ACA-96): the server refuses the booking if
        // its price differs, so nothing is charged that they did not see.
        quotes: shownQuotes(basket),
      }

      // The tyres go as their own field (ACA-98): sizes and quantities per axle,
      // and the chosen offers, which the server prices again and turns into job
      // lines and the garage's tyre note. The axle totals the customer was shown
      // go too, so the server refuses a tyre price that moved since.
      const tyresPriced = tyreTotal > 0
      if (tyreSelection) {
        body.tyreSelection = toTyreSelectionRequest(tyreSelection, {
          frontOfferId: tyresPriced ? selectedOffer?.id ?? null : null,
          rearOfferId: tyresPriced ? selectedRearOffer?.id ?? null : null,
          ...shownTyreTotals(tyreSelection, selectedOffer, selectedRearOffer),
        })
      }

      const notes = joinBookingNotes([
        // The server resolves the vehicle from the registration and ignores a
        // typed make and model, so the garage only sees them as a note.
        !vehicle && manualVehicle?.make.trim()
          ? `Vehicle as entered: ${manualVehicle.make.trim()} ${manualVehicle.model.trim()}`.trim()
          : null,
        specialRequests.trim() || null,
      ])

      if (notes) body.specialRequests = notes

      if (vehicle) {
        // Null when the car was found through DVSA rather than held by the
        // garage. The server resolves the vehicle from the registration anyway.
        if (vehicle.vehicleId) body.vehicleId = vehicle.vehicleId
        body.vehicleMake = vehicle.vehicleMake
        body.vehicleModel = vehicle.vehicleModel
      } else if (manualVehicle) {
        body.vehicleMake = manualVehicle.make.trim()
        body.vehicleModel = manualVehicle.model.trim()
      }

      // Saved before the redirect: Checkout is a full page navigation, and the
      // confirmation after it needs everything the customer chose.
      const holdForPayment = (held: HeldBooking) => {
        heldBooking.current = held
        savePendingBooking(config.publicKey, {
          version: 1,
          savedAt: Date.now(),
          held,
          basket,
          selectedJobKeys,
          slot: selectedSlot,
          plan,
          registration: vehicleReg,
          // As priced, with any answer to "What is it?" (ACA-110) in it.
          vehicle: pricedVehicle,
          manualVehicle,
          details,
          specialRequests,
          tyreSelection,
        })
      }

      // Inside a frame Checkout replaces the page around it, so that is where
      // the customer comes back to (ACA-99).
      const returnUrls = checkoutReturnUrls(window, config.returnUrl ?? null)
      const outcome = await submitBookingFlow(bookingApi(tokenValue, returnUrls), {
        body,
        fingerprint: bookingFingerprint({
          serviceIds: body.serviceIds,
          slotStart: body.slotStart,
          vehicleReg,
          customerEmail: body.customerEmail,
          tyres: body.tyreSelection ? JSON.stringify(body.tyreSelection) : null,
        }),
        // Only hand off to Stripe when the channel actually takes money.
        requiresPayment: requiresCheckout(plan),
        held: heldBooking.current,
        returnUrl: returnUrls.returnUrl,
        onCreated: (created) => {
          emit('booking_created', {
            bookingId: created.bookingId,
            reference: bookingReference(created.reference, created.bookingId),
          })
          trackConversion('booking_created', {
            ...basketConversionDetails,
            paymentMode: plan.mode,
            bookingStatus: bookingOutcome(created.status, requiresCheckout(plan)),
          })
        },
        onHold: holdForPayment,
      })

      if (outcome.kind === 'checkout') {
        const { checkoutUrl, held, endsOnHostedPage } = outcome
        hostedPageCountsPayment.current = endsOnHostedPage
        const route = goToCheckout(checkoutUrl, {
          // Ending on the hosted page (ACA-111), this page never sees the
          // return: the mark says so, so a later visit only settles it quietly.
          onRoute: (via) =>
            markPendingCheckout(config.publicKey, {
              route: via,
              checkoutUrl,
              ...(endsOnHostedPage ? { endsOnHostedPage: true } : {}),
            }),
        })
        if (route === 'newTab') {
          setCheckoutTabError(null)
          setCheckoutTab({ bookingId: held.bookingId, sessionId: held.sessionId, checkoutUrl })
        }
        return
      }

      if (outcome.kind === 'slotUnavailable') {
        offerOtherTimes()
        return
      }

      if (outcome.kind === 'processing') {
        setProcessingPayment({ bookingId: outcome.bookingId, sessionId: outcome.sessionId })
        return
      }

      if (outcome.kind === 'priceChanged') {
        // Nothing was booked. Look the car up again so the basket shows today's prices for it,
        // then let the customer check them and book again.
        setSubmitError(PRICE_CHANGED_MESSAGE)
        emit('error', { message: PRICE_CHANGED_MESSAGE, stage: 'booking' })
        if (vehicleReg) await lookupVehicle(vehicleReg)
        // Priced tyres (ACA-98): fetch the offers again and keep the chosen tyres at today's
        // price, or drop one that has gone so the customer chooses again.
        const tyresSent = body.tyreSelection
        if (tyreSelection && (tyresSent?.frontOfferId || tyresSent?.rearOfferId)) {
          const fresh = await loadTyreOffers(tyreSelection)
          setSelectedOffer((previous) => refreshedOffer(previous, fresh?.front ?? []))
          setSelectedRearOffer((previous) => refreshedOffer(previous, fresh?.rear ?? []))
        }
        return
      }

      heldBooking.current = null
      clearPendingBooking(config.publicKey)
      setConfirmed({
        bookingId: outcome.bookingId,
        reference: bookingReference(outcome.reference, outcome.bookingId),
        // Paid: what was charged, or the plan when the server did not say.
        // Not paid: the server's word on what is due, which is nothing.
        plan: outcome.paid
          ? planAfterBooking(plan, outcome.payment ?? undefined)
          : planAfterBooking(plan, outcome.payment),
        status: outcome.status,
      })
      setStepId('confirmation')
      if (outcome.paid) {
        emit('payment_completed', { bookingId: outcome.bookingId })
        trackConversion('payment_completed', {
          ...basketConversionDetails,
          paymentMode: plan.mode,
        })
      }
    } catch (error) {
      if (isSlotConflict(error)) {
        offerOtherTimes()
        return
      }
      const typeNeeded = vehicleTypeNeededMessage(error)
      if (typeNeeded) {
        // The server has no answer for this car: ask again, from nothing, where the question lives.
        vehicleTypeNotice.current = typeNeeded
        forgetVehicleType()
        const target = steps.find((step) => step.id === 'build' || step.id === 'extras')
        if (target) {
          setNavDirection('back')
          setStepId(target.id)
        }
        setVehicleTypeFocus((n) => n + 1)
      }
      const message =
        typeNeeded ??
        (hasApiErrorCode(error, 'MOT_CLASS_NOT_TESTED')
          ? motNotTestedMessage(channel?.contactPhone)
          : hasApiErrorCode(error, 'SERVICE_NOT_FOR_VEHICLE')
            ? serviceNotForVehicleMessage(channel?.contactPhone)
            : getErrorMessage(error))
      setSubmitError(message)
      emit('error', { message, stage: 'booking' })
    } finally {
      setSubmitting(false)
    }
  }, [
    basket,
    basketConversionDetails,
    bookingApi,
    channel?.contactPhone,
    channel?.timezone,
    config.publicKey,
    details,
    emit,
    freshToken,
    loadTyreOffers,
    lookupVehicle,
    manualVehicle,
    plan,
    pricedVehicle,
    registration,
    selectedJobKeys,
    selectedSlot,
    selectedOffer,
    selectedRearOffer,
    specialRequests,
    steps,
    trackConversion,
    tyreSelection,
    tyreTotal,
    vehicle,
    forgetVehicleType,
  ])

  // "What is it?" asked again, or still unanswered: put the customer on it.
  useEffect(() => {
    if (vehicleTypeFocus) vehicleTypeRef.current?.querySelector('input')?.focus()
  }, [vehicleTypeFocus])

  const goNext = useCallback(() => {
    const error = validateStep(stepId)
    if (error) {
      setSubmitError(error)
      return
    }

    setSubmitError(null)

    // The final step submits; everything else advances.
    if (nextStep(steps, stepId) === null || nextStep(steps, stepId)?.terminal) {
      if (stepId === 'details') trackConversion('details_entered', basketConversionDetails)
      void submitBooking()
      return
    }

    if (stepId === 'build' || stepId === 'tyres') {
      emit('services_selected', { serviceIds: basket.map((item) => item.serviceId), total })
    }

    // Funnel steps (ACA-107), each reported once per visit however often the
    // customer passes this way.
    if (stepId === 'vehicle') {
      trackConversion('vehicle_found', {
        vehicle: lookupStatus === 'success' ? 'found' : vehicleSkipped ? 'skipped' : 'entered',
      })
    }
    // A tyre-only journey identifies the car, or just its size, on its first screen.
    if (stepId === 'tyres' && flowType === 'tyres') {
      trackConversion('vehicle_found', { vehicle: lookupStatus === 'success' ? 'found' : 'size' })
    }
    if (SERVICE_STEPS.has(stepId) && basket.length > 0) {
      trackConversion('service_chosen', basketConversionDetails)
    }
    if (stepId === 'schedule') trackConversion('time_chosen', basketConversionDetails)

    const target = nextStep(steps, stepId)
    if (!target) return

    setNavDirection('forward')
    setStepId(target.id)
    emit('step', { step: target.id, index: steps.indexOf(target) })
  }, [
    basket,
    basketConversionDetails,
    emit,
    flowType,
    lookupStatus,
    stepId,
    steps,
    submitBooking,
    total,
    trackConversion,
    validateStep,
    vehicleSkipped,
  ])

  // ----- Deep links -----
  // Read by the lookup's completion, which resolves renders after it started.
  const stepsRef = useRef(steps)
  const stepIdRef = useRef(stepId)
  useEffect(() => {
    stepsRef.current = steps
    stepIdRef.current = stepId
  }, [steps, stepId])

  useEffect(() => {
    if (typeof window === 'undefined' || !bootstrap) return

    const apply = () => {
      const url = new URL(window.location.href)
      // A payment return restores its own booking; a link must not overwrite it.
      if (parsePaymentReturn(url).isReturn) return

      const request = parseExternalBookingRequest(url)
      if (!request.shouldOpen && !request.registration) return

      if (request.shouldOpen) onOpenChange(true)

      if (request.serviceKey && url.hash !== lastDeepLinkService.current) {
        lastDeepLinkService.current = url.hash
        const choice = resolveDeepLinkService(jobTypes, request.serviceKey)

        if (choice?.service) {
          // One obvious service: add it, as tapping its card would, in place of
          // anything else already chosen from that category.
          const linkedJob = choice.jobType
          const linkedService = choice.service
          const jobKey = linkedJob.key
          setBasket((previous) =>
            previous.some((entry) => entry.serviceId === linkedService.id)
              ? previous
              : chooseInCategory(previous, linkedJob, linkedService)
          )
          setSelectedJobKeys((previous) => (previous.includes(jobKey) ? previous : [...previous, jobKey]))
          setDeepLinkJobKey(null)
        } else if (choice) {
          // A real choice: open the category and let the customer make it.
          setDeepLinkJobKey(choice.jobType.key)
        }
      }

      if (request.registration && request.registration !== lastDeepLinkReg.current) {
        lastDeepLinkReg.current = request.registration
        setRegistration(request.registration)

        void lookupVehicle(request.registration).then((found) => {
          // Move on only from the vehicle step, and only if the customer is
          // still on it: they may have skipped ahead while the lookup ran.
          if (!found || stepIdRef.current !== 'vehicle') return
          const target = nextStep(stepsRef.current, 'vehicle')
          if (!target) return

          setNavDirection('forward')
          setStepId(target.id)
          emit('step', { step: target.id, index: stepsRef.current.indexOf(target) })
        })
      }
    }

    apply()
    window.addEventListener('hashchange', apply)
    window.addEventListener('popstate', apply)
    return () => {
      window.removeEventListener('hashchange', apply)
      window.removeEventListener('popstate', apply)
    }
  }, [bootstrap, emit, jobTypes, lookupVehicle, onOpenChange])

  // ----- Return from Stripe -----
  /** Puts back everything the customer chose before the Checkout redirect. */
  const restoreSnapshot = (snapshot: PendingBookingSnapshot) => {
    restoredBasketKey.current = snapshot.basket.map((item) => item.serviceId).join(',')
    heldBooking.current = snapshot.held

    setBasket(snapshot.basket)
    setSelectedJobKeys(snapshot.selectedJobKeys)
    setSelectedSlot(snapshot.slot)

    const day = slotDate(snapshot.slot.startTime)
    if (day) {
      setSelectedDate(day)
      setMonthCursor(startOfMonth(day))
    }

    setRegistration(snapshot.registration)
    lastDeepLinkReg.current = snapshot.registration
    setVehicle(snapshot.vehicle)
    setManualVehicle(snapshot.manualVehicle)
    setLookupStatus(snapshot.vehicle ? 'success' : 'idle')
    setLookupError(null)
    setVehicleSkipped(!snapshot.vehicle && !snapshot.manualVehicle)
    setDetails(snapshot.details)
    setSpecialRequests(snapshot.specialRequests)
    setTyreSelection(snapshot.tyreSelection)
    // They accepted the terms to reach Checkout.
    setTermsAccepted(true)
  }

  const applyPaymentReturn = (outcome: PaymentReturnOutcome) => {
    if (outcome.kind === 'none') return
    if (outcome.snapshot) restoreSnapshot(outcome.snapshot)

    // With nothing restored there is nothing to retry from, so start again.
    const retryStep: StepId = outcome.snapshot ? 'details' : (steps[0]?.id ?? 'vehicle')

    switch (outcome.kind) {
      case 'cancelled':
        setStepId(retryStep)
        setSubmitError(
          outcome.snapshot
            ? 'Payment cancelled. Your time is held for a short while, so you can try again.'
            : 'Payment cancelled. Nothing was charged.'
        )
        return
      case 'paid':
        heldBooking.current = null
        clearPendingBooking(config.publicKey)
        setConfirmed({
          bookingId: outcome.bookingId,
          reference: bookingReference(outcome.reference, outcome.bookingId),
          plan: outcome.snapshot
            ? planAfterBooking(outcome.snapshot.plan, outcome.snapshot.held.payment ?? undefined)
            : plan,
          status: outcome.status,
        })
        setStepId('confirmation')
        emit('payment_completed', { bookingId: outcome.bookingId })
        trackConversion('payment_completed', {
          ...basketConversionDetails,
          paymentMode: outcome.snapshot?.plan.mode ?? plan.mode,
          priceBand: priceBand(outcome.snapshot?.plan.total ?? total),
        })
        return
      case 'unpaid':
        setStepId(retryStep)
        setSubmitError('That payment did not complete. Please try again.')
        return
      case 'processing':
        // Klarna has the payment and Stripe will confirm it shortly; the time
        // stays held, so wait here rather than offering to pay again.
        setStepId(retryStep)
        setProcessingPayment({ bookingId: outcome.bookingId, sessionId: outcome.sessionId })
        return
      case 'expired':
        // The hold ran out, so the time was released. Keep everything else.
        heldBooking.current = null
        clearPendingBooking(config.publicKey)
        setSelectedSlot(null)
        setStepId(outcome.snapshot ? 'schedule' : retryStep)
        setSubmitError(expiredHoldMessage(outcome))
        return
      case 'failed':
        setStepId(retryStep)
        setSubmitError(getErrorMessage(outcome.error))
        return
    }
  }

  /**
   * The customer left Checkout without paying (ACA-101). Once the server has
   * freed the time they pick one again; a booking paid after all is
   * confirmed; a hold that could not be released still stands for a retry.
   */
  const applyCheckoutLeft = (
    left: CheckoutCancelOutcome,
    bookingId: UUID,
    snapshot: PendingBookingSnapshot | null
  ) => {
    switch (left.kind) {
      case 'paid':
        applyPaymentReturn({
          kind: 'paid',
          bookingId,
          status: left.status,
          reference: left.reference ?? snapshot?.held.reference ?? null,
          snapshot,
        })
        return
      case 'held':
        applyPaymentReturn({ kind: 'cancelled', bookingId, snapshot })
        return
      case 'released':
        if (snapshot) restoreSnapshot(snapshot)
        heldBooking.current = null
        clearPendingBooking(config.publicKey)
        // The freed time is offered again with everything else: fetch afresh.
        slotCache.current.clear()
        setCapacityByDate({})
        setSlots([])
        setSelectedSlot(null)
        setNavDirection('back')
        setStepId(snapshot ? 'schedule' : (steps[0]?.id ?? 'vehicle'))
        setSubmitError(snapshot ? CHECKOUT_LEFT_MESSAGE : CHECKOUT_LEFT_NO_SNAPSHOT_MESSAGE)
        return
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !bootstrap) return

    const ownReturn = parsePaymentReturn(new URL(window.location.href))
    const snapshot = readPendingBooking(config.publicKey)
    // Inside a frame Checkout comes back to the page around it (ACA-99): its
    // parameters when that page can be read, else the snapshot says what was
    // going on.
    const framedReturn = ownReturn.isReturn ? null : readFramedPaymentReturn(window)
    const resume: SnapshotResume =
      ownReturn.isReturn || framedReturn ? { kind: 'none' } : resumeFromSnapshot(snapshot)

    if (resume.kind === 'settleHosted') {
      // Checkout ended on the hosted booking page, which showed how it went:
      // nothing to show here, only a hold to free if they came Back unpaid.
      void (async () => {
        try {
          const tokenValue = await freshToken()
          const settled = await settleHostedCheckout(
            bookingApi(tokenValue),
            resume.bookingId,
            resume.sessionId
          )
          if (settled) clearPendingBooking(config.publicKey)
        } catch {
          // No token just now: the next visit tries again.
        }
      })()
      return
    }

    if (resume.kind === 'waitInNewTab' && snapshot) {
      // The visit that chose all this was counted before Checkout took over.
      conversion.current?.markReported(STEPS_BEFORE_PAYMENT)
      hostedPageCountsPayment.current = snapshot.checkout?.endsOnHostedPage === true
      restoreSnapshot(snapshot)
      setStepId('details')
      onOpenChange(true)
      setCheckoutTab({
        bookingId: resume.bookingId,
        sessionId: resume.sessionId,
        checkoutUrl: resume.checkoutUrl,
      })
      return
    }

    const paymentReturn =
      framedReturn ?? (resume.kind === 'confirm' ? resume.paymentReturn : ownReturn)
    if (!paymentReturn.isReturn) return

    const cleanUrl = () => {
      if (!ownReturn.isReturn) return
      window.history.replaceState(null, document.title, stripWidgetParams(window.location.href))
    }

    const actionable =
      paymentReturn.cancelled ||
      Boolean(paymentReturn.sessionId && (paymentReturn.bookingId || snapshot))
    if (!actionable) {
      cleanUrl()
      return
    }

    conversion.current?.markReported(STEPS_BEFORE_PAYMENT)
    onOpenChange(true)
    setPaymentBusy(true)

    void (async () => {
      try {
        const tokenValue = await freshToken()
        const calls = bookingApi(tokenValue)
        const outcome = await resolvePaymentReturn({
          paymentReturn,
          snapshot,
          confirmPayment: calls.confirmPayment,
        })
        // Back without paying, by Checkout's back link or (for a page Checkout
        // replaced) any other way: free the time now rather than in 30 minutes.
        const leftCheckout =
          outcome.kind === 'cancelled' || (resume.kind === 'confirm' && outcome.kind === 'unpaid')
        if (leftCheckout) {
          if (outcome.bookingId) {
            const sessionId = paymentReturn.sessionId ?? outcome.snapshot?.held.sessionId ?? null
            const left = await cancelCheckout(calls, outcome.bookingId, sessionId)
            applyCheckoutLeft(left, outcome.bookingId, outcome.snapshot)
            return
          }
        }
        applyPaymentReturn(outcome)
      } catch (error) {
        setSubmitError(getErrorMessage(error))
      } finally {
        setPaymentBusy(false)
        cleanUrl()
      }
    })()
    // Runs once per mount, after the channel is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap])

  // Opened: the customer can see the widget, which needs the channel. Declared
  // after the payment return above so a return marks its visit first.
  useEffect(() => {
    if (!open || !bootstrap) return
    trackConversion('widget_opened')
  }, [bootstrap, open, trackConversion])

  // ----- Paying in another tab (ACA-99) -----
  /** Settles the wait once Stripe has an answer. True when it has; false to keep waiting. */
  const pollCheckoutTab = async (): Promise<boolean> => {
    const tab = checkoutTab
    if (!tab) return true
    if (!tab.sessionId) return false

    const tokenValue = await freshToken()
    const confirmation = interpretPaymentConfirmation(
      await bookingApi(tokenValue).confirmPayment(tab.bookingId, tab.sessionId)
    )
    if (confirmation.kind === 'unpaid') return false

    const saved = readPendingBooking(config.publicKey)
    const snapshot = saved?.held.bookingId === tab.bookingId ? saved : null
    setCheckoutTab(null)
    if (confirmation.kind === 'processing') {
      setProcessingPayment({ bookingId: tab.bookingId, sessionId: tab.sessionId })
    } else if (confirmation.kind === 'paid') {
      applyPaymentReturn({
        kind: 'paid',
        bookingId: tab.bookingId,
        status: confirmation.status,
        reference: confirmation.reference ?? snapshot?.held.reference ?? null,
        snapshot,
      })
    } else {
      applyPaymentReturn({
        kind: 'expired',
        bookingId: tab.bookingId,
        paid: confirmation.paid,
        refunded: confirmation.refunded,
        snapshot,
      })
    }
    return true
  }

  // ----- A Klarna payment still settling (ACA-105) -----
  /** Settles the wait once Stripe has an answer. True when it has; false to keep waiting. */
  const pollProcessingPayment = async (): Promise<boolean> => {
    const waiting = processingPayment
    if (!waiting) return true

    const tokenValue = await freshToken()
    const confirmation = interpretPaymentConfirmation(
      await bookingApi(tokenValue).confirmPayment(waiting.bookingId, waiting.sessionId)
    )
    if (confirmation.kind === 'processing') return false

    const saved = readPendingBooking(config.publicKey)
    const snapshot = saved?.held.bookingId === waiting.bookingId ? saved : null
    setProcessingPayment(null)
    switch (confirmation.kind) {
      case 'paid':
        applyPaymentReturn({
          kind: 'paid',
          bookingId: waiting.bookingId,
          status: confirmation.status,
          reference: confirmation.reference ?? snapshot?.held.reference ?? null,
          snapshot,
        })
        break
      case 'expired':
        applyPaymentReturn({
          kind: 'expired',
          bookingId: waiting.bookingId,
          paid: confirmation.paid,
          refunded: confirmation.refunded,
          snapshot,
        })
        break
      case 'unpaid':
        applyPaymentReturn({ kind: 'unpaid', bookingId: waiting.bookingId, snapshot })
        break
    }
    return true
  }

  /** The customer gives up on the payment in the other tab: free the time (ACA-101). */
  const leaveCheckoutTab = async () => {
    const tab = checkoutTab
    if (!tab) return

    setPaymentBusy(true)
    setCheckoutTabError(null)
    try {
      const tokenValue = await freshToken()
      const left = await cancelCheckout(bookingApi(tokenValue), tab.bookingId, tab.sessionId)
      if (left.kind === 'held') {
        setCheckoutTabError(CHECKOUT_TAB_CANCEL_FAILED)
        return
      }
      const saved = readPendingBooking(config.publicKey)
      setCheckoutTab(null)
      applyCheckoutLeft(left, tab.bookingId, saved?.held.bookingId === tab.bookingId ? saved : null)
    } catch {
      setCheckoutTabError(CHECKOUT_TAB_CANCEL_FAILED)
    } finally {
      setPaymentBusy(false)
    }
  }

  const startAgain = useCallback(() => {
    setStepId(steps[0]?.id ?? 'build')
    setSelectedJobKeys([])
    setTyreSelection(null)
    setTyreOffers([])
    setRearTyreOffers([])
    setTyreOffersUnavailable(false)
    setTyreFilters(EMPTY_FILTERS)
    setRearTyreFilters(EMPTY_FILTERS)
    setSelectedOffer(null)
    setSelectedRearOffer(null)
    setBasket([])
    setSelectedDate(null)
    setSelectedSlot(null)
    setSlots([])
    setSpecialRequests('')
    setDetails({ name: '', email: '', phone: '' })
    setDetailErrors({})
    setTermsAccepted(false)
    setSubmitError(null)
    setConfirmed(null)
    heldBooking.current = null
    setDeepLinkJobKey(null)
    setVehicle(null)
    setManualVehicle(null)
    forgetVehicleType()
    setRegistration('')
    setLookupStatus('idle')
    setLookupError(null)
    setVehicleSkipped(false)
  }, [forgetVehicleType, steps])

  // Nothing renders until the channel is known: a half-configured widget on a
  // customer's homepage is worse than none.
  if (!bootstrap || loadingBootstrap || bootstrapError || !channel) return null

  const vehicleLabel = vehicle
    ? `${registration} · ${vehicle.vehicleMake} ${vehicle.vehicleModel}`
    : manualVehicle?.make
      ? `${registration} · ${manualVehicle.make} ${manualVehicle.model}`.trim()
      : registration || null

  const termsUrl = channel.termsUrl?.trim() || AUTOCHAIN_TERMS

  const isFinalStep = nextStep(steps, stepId)?.terminal ?? false

  const primaryLabel = (() => {
    if (submitting || paymentBusy) return 'Please wait…'
    if (isFinalStep) return plan.actionLabel
    return 'Continue'
  })()

  const actions =
    stepId === 'confirmation' ? null : (
      <>
        {submitError ? (
          <div className="ac-notice ac-notice--error" role="alert">
            <AlertIcon size={18} />
            <span>{submitError}</span>
          </div>
        ) : null}

        {isFinalStep && requiresCheckout(plan) && cancellationPolicyText(channel.cancellationPolicy) ? (
          <p className="ac-hint">{cancellationPolicyText(channel.cancellationPolicy)}</p>
        ) : null}

        {isFinalStep ? (
          <label className="ac-check">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
            />
            <span>
              I accept the{' '}
              <a href={termsUrl} target="_blank" rel="noreferrer noopener">
                terms and conditions
              </a>
              .
            </span>
          </label>
        ) : null}

        {/*
          A tyre channel puts the fitting in the basket before the customer has
          done anything, so the opening screen announced "1 item · Total £22" —
          a number they did not choose, for a thing they did not ask for, next to
          the tyres they came to price. The basket appears once there is a tyre
          in it to talk about.
        */}
        {(flowType !== 'tyres' || tyreSelection != null) && (
          <BasketBar
            basket={displayBasket}
            tyreLine={
              tyreSelection && tyreDescription
                ? { description: tyreDescription, total: tyreTotal }
                : null
            }
            total={totalDisplay}
            showPrices={showPrices}
            onRemoveService={removeServiceFromBasket}
          />
        )}

        <button
          type="button"
          className="ac-btn ac-btn--primary"
          onClick={goNext}
          disabled={submitting || paymentBusy}
        >
          {submitting || paymentBusy ? <span className="ac-spinner" aria-hidden="true" /> : null}
          {primaryLabel}
        </button>

      </>
    )

  if (showLauncher && !open) {
    // The launcher says what this widget is for. A tyre shop's drawer button
    // reading "Book now" tells a customer nothing about whether their size is
    // in there, and a tyre price is the thing they came to find.
    const tyresOnly = flowType === 'tyres'

    return (
      <div style={themeStyle} data-ac-theme={themeMode}>
        <button
          ref={launcherRef}
          type="button"
          className="ac-launcher"
          onClick={() => {
            openedFromLauncher.current = true
            onOpenChange(true)
          }}
        >
          <span className="ac-launcher__icon">
            {tyresOnly ? <JobIcon name="tyres" size={18} /> : <CalendarIcon size={18} />}
          </span>
          {tyresOnly ? 'Tyre prices' : flowType === 'mot' ? 'Book your MOT' : 'Book now'}
        </button>
      </div>
    )
  }

  if (processingPayment) {
    return (
      <Shell
        displayMode={displayMode}
        open={open}
        onClose={() => onOpenChange(false)}
        title={channel.businessDisplayName || 'Book online'}
        subtitle={channel.siteDisplayName}
        logoUrl={channel.logoUrl}
        themeStyle={themeStyle}
        themeMode={themeMode}
        progress={null}
        stepKey="payment-processing"
        direction="forward"
        onBack={null}
        actions={null}
        footer={<span>Powered by AutoChain</span>}
      >
        <PaymentProcessingWait poll={pollProcessingPayment} />
      </Shell>
    )
  }

  if (checkoutTab) {
    return (
      <Shell
        displayMode={displayMode}
        open={open}
        onClose={() => onOpenChange(false)}
        title={channel.businessDisplayName || 'Book online'}
        subtitle={channel.siteDisplayName}
        logoUrl={channel.logoUrl}
        themeStyle={themeStyle}
        themeMode={themeMode}
        progress={null}
        stepKey="checkout-tab"
        direction="forward"
        onBack={null}
        actions={null}
        footer={<span>Powered by AutoChain</span>}
      >
        <CheckoutTabWait
          checkoutUrl={checkoutTab.checkoutUrl}
          poll={pollCheckoutTab}
          onChooseAnotherTime={() => void leaveCheckoutTab()}
          busy={paymentBusy}
          error={checkoutTabError}
        />
      </Shell>
    )
  }

  return (
    <Shell
      displayMode={displayMode}
      open={open}
      onClose={() => onOpenChange(false)}
      title={channel.businessDisplayName || 'Book online'}
      subtitle={channel.siteDisplayName}
      logoUrl={channel.logoUrl}
      themeStyle={themeStyle}
      themeMode={themeMode}
      progress={shownProgress}
      stepKey={stepId}
      direction={navDirection}
      onBack={stepId !== 'confirmation' && previousStep(steps, stepId) ? goBack : null}
      actions={actions}
      footer={<span>Powered by AutoChain</span>}
    >
      {stepId === 'vehicle' ? (
        <VehicleStep
          businessName={channel.businessDisplayName || 'us'}
          registration={registration}
          onRegistrationChange={(value) => {
            setRegistration(value)
            setVehicle(null)
            forgetVehicleType()
            setLookupStatus('idle')
            setLookupError(null)
            setVehicleSkipped(false)
          }}
          onLookup={() => void lookupVehicle(registration)}
          status={lookupStatus}
          vehicle={vehicle}
          error={lookupError}
          lookupRequired={lookupRequired}
          manualVehicle={manualVehicle}
          onManualVehicleChange={setManualVehicle}
          onSkip={
            lookupRequired
              ? undefined
              : () => {
                  setLookupError(null)
                  setVehicleSkipped(true)
                  const target = nextStep(steps, 'vehicle')
                  if (target) setStepId(target.id)
                }
          }
        />
      ) : null}

      {askVehicleType && (stepId === 'build' || stepId === 'extras') ? (
        <MotVehicleTypeQuestion
          value={vehicleTypeAnswer}
          onChange={changeVehicleType}
          busy={vehicleTypeBusy}
          error={vehicleTypeError}
          fieldsetRef={vehicleTypeRef}
        />
      ) : null}

      {motUnavailable && (stepId === 'build' || stepId === 'extras') ? (
        <div className="ac-notice ac-notice--info" role="status">
          {motUnavailable}
        </div>
      ) : null}

      {evMessage && (stepId === 'build' || stepId === 'extras') ? (
        <div className="ac-notice ac-notice--info" role="status">
          {evMessage}
        </div>
      ) : null}

      {stepId === 'build' ? (
        <BuildStep
          jobTypes={jobTypes}
          basket={basket}
          onToggleService={toggleServiceFromExtras}
          showPrices={showPrices}
          loading={loadingBootstrap}
          vehicleLabel={vehicleLabel}
          motPrompt={motPrompt}
          upsellPrompts={upsellPrompts}
          tyreFromPrice={normalizePrice(
            (channel as { tyreFromPrice?: unknown } | null)?.tyreFromPrice
          )}
          initialExpandedKey={deepLinkJobKey}
        />
      ) : null}

      {stepId === 'tyres' && flowType === 'tyres' ? (
        <TyreStartStep
          businessName={channel.businessDisplayName || 'Book online'}
          registration={registration}
          onRegistrationChange={(value) => {
            setRegistration(value)
            setVehicle(null)
            forgetVehicleType()
            setLookupStatus('idle')
            setLookupError(null)
          }}
          onLookup={() => void lookupVehicle(registration)}
          lookupStatus={lookupStatus}
          vehicle={vehicle}
          lookupError={lookupError}
          vehicleSizes={toSizeChoices(vehicle?.tyreSizes)}
          fitments={tyreFitments}
          selection={tyreSelection}
          onSelectionChange={setTyreSelection}
        />
      ) : null}

      {stepId === 'tyres' && flowType !== 'tyres' ? (
        <TyreStep
          vehicleSizes={toSizeChoices(vehicle?.tyreSizes)}
          fitments={tyreFitments}
          selection={tyreSelection}
          onSelectionChange={setTyreSelection}
          fittingPrice={tyreFittingPrice}
          showPrices={showPrices}
          vehicleLabel={vehicleLabel}
        />
      ) : null}

      {stepId === 'tyreOffers' && tyreSelection && !tyreSelection.rear ? (
        <TyreOffersStep
          size={tyreSelection.size.size}
          quantity={tyreSelection.quantity}
          offers={tyreOffers}
          loading={tyreOffersLoading}
          error={tyreOffersError}
          onRetry={() => void loadTyreOffers(tyreSelection)}
          filters={tyreFilters}
          onFiltersChange={setTyreFilters}
          selectedOfferId={selectedOffer?.id ?? null}
          onSelectOffer={setSelectedOffer}
          showPrices={showPrices}
          promotions={tyrePromotions}
          onQuantityChange={(quantity) =>
            setTyreSelection((previous) => (previous ? { ...previous, quantity } : previous))
          }
          runFlat={tyreSelection.runFlat === true}
        />
      ) : null}

      {/* A staggered car (ACA-98): a choice per axle being fitted, each for its own count. */}
      {stepId === 'tyreOffers' && tyreSelection?.rear ? (
        <div className="ac-stack">
          {tyreSelection.quantity > 0 ? (
            <TyreOffersStep
              axle="front"
              size={tyreSelection.size.size}
              quantity={tyreSelection.quantity}
              offers={tyreOffers}
              loading={tyreOffersLoading}
              error={tyreOffersError}
              onRetry={() => void loadTyreOffers(tyreSelection)}
              filters={tyreFilters}
              onFiltersChange={setTyreFilters}
              selectedOfferId={selectedOffer?.id ?? null}
              onSelectOffer={setSelectedOffer}
              showPrices={showPrices}
              promotions={[]}
              onQuantityChange={() => undefined}
              runFlat={tyreSelection.runFlat === true}
            />
          ) : null}
          {tyreSelection.rear.quantity > 0 ? (
            <TyreOffersStep
              axle="rear"
              size={tyreSelection.rear.size}
              quantity={tyreSelection.rear.quantity}
              offers={rearTyreOffers}
              loading={tyreOffersLoading}
              error={tyreOffersError}
              onRetry={() => void loadTyreOffers(tyreSelection)}
              filters={rearTyreFilters}
              onFiltersChange={setRearTyreFilters}
              selectedOfferId={selectedRearOffer?.id ?? null}
              onSelectOffer={setSelectedRearOffer}
              showPrices={showPrices}
              promotions={[]}
              onQuantityChange={() => undefined}
              runFlat={tyreSelection.runFlat === true && tyreSelection.quantity === 0}
            />
          ) : null}
        </div>
      ) : null}

      {stepId === 'extras' && flowType === 'tyres' ? (
        <TyreAttachStep
          available={jobsWithRoomToAdd.filter(
            (jobType) => matchJobIcon(jobType.key) !== 'tyres'
          )}
          basket={basket}
          onToggleService={toggleServiceFromExtras}
          showPrices={showPrices}
        />
      ) : null}

      {stepId === 'extras' && flowType !== 'tyres' ? (
        <ExtrasStep
          available={
            // The MOT is already the promo when it's due; listing it again
            // below reads as a mistake.
            // An MOT inside a wider category leaves that category listed.
            motPrompt && !motPrompt.service
              ? jobsWithRoomToAdd.filter((jobType) => jobType.key !== motPrompt.jobType.key)
              : jobsWithRoomToAdd
          }
          basket={displayBasket}
          onToggleService={toggleServiceFromExtras}
          onRemoveService={removeServiceFromBasket}
          showPrices={showPrices}
          total={total}
          tyreLine={pricedTyreLine}
          motPrompt={motPrompt}
          upsellPrompts={upsellPrompts}
        />
      ) : null}

      {stepId === 'details' ? (
        <DetailsStep
          registration={
            registration.trim()
              ? null
              : {
                  value: registration,
                  onChange: (value) => {
                    setRegistration(value)
                    setDetailErrors(({ registration: _ignored, ...rest }) => rest)
                  },
                  error: detailErrors.registration ?? null,
                }
          }
          details={details}
          onDetailsChange={setDetails}
          specialRequests={specialRequests}
          onSpecialRequestsChange={setSpecialRequests}
          fieldErrors={detailErrors}
        />
      ) : null}

      {stepId === 'schedule' ? (
        <ScheduleStep
          monthCursor={monthCursor}
          onMonthChange={setMonthCursor}
          minDate={minDate}
          maxDate={maxDate}
          capacityByDate={capacityByDate}
          monthLoading={monthLoading}
          selectedDate={selectedDate}
          onSelectDate={(date) => {
            // The effect above fetches the day's times.
            setSelectedDate(date)
            setSelectedSlot(null)
          }}
          slots={slots}
          slotsLoading={slotsLoading}
          slotsError={slotsError}
          onRetrySlots={() => {
            if (!selectedDate) return
            slotCache.current.delete(toLocalDateString(selectedDate))
            void loadSlots(selectedDate)
          }}
          selectedSlot={selectedSlot}
          onSelectSlot={(slot) => {
            setSelectedSlot(slot)
            setSubmitError(null)
            emit('slot_selected', { startTime: slot.startTime })
          }}
        />
      ) : null}

      {stepId === 'confirmation' ? (
        <ConfirmationStep
          reference={confirmed?.reference ?? ''}
          status={confirmed?.status ?? null}
          tyreLine={pricedTyreLine}
          businessName={channel.businessDisplayName}
          slot={selectedSlot}
          basket={displayBasket}
          showPrices={showPrices}
          plan={confirmed?.plan ?? plan}
          vehicleLabel={vehicleLabel}
          contactPhone={channel.contactPhone}
          contactAddress={channel.contactAddress}
          calendarHref={
            selectedSlot
              ? buildIcsDataUrl({
                  reference: confirmed?.reference ?? '',
                  businessName: channel.businessDisplayName,
                  summary: bookingCalendarSummary(
                    channel.businessDisplayName,
                    basket.map((item) => item.name)
                  ),
                  location: channel.contactAddress,
                  start: selectedSlot.startTime,
                  end: selectedSlot.endTime,
                })
              : null
          }
          onBookAnother={startAgain}
        />
      ) : null}
    </Shell>
  )
}

/** A price worth quoting: free or unpriced reads as nothing to show. */
function paidPrice(price: unknown): number | null {
  const value = normalizePrice(price)
  return value != null && value > 0 ? value : null
}
